import { useEffect, useState } from "react";
import { Heart, Loader2, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCustomerAuth } from "@/context/AuthContext";
import { PocketShareButtons } from "@/components/PocketShareButtons";

/**
 * Heart button that bookmarks the active Beats-Store filter combo
 * (genre + bpm range + key) to the signed-in artist's account.
 * Daily digest emails are sent via the saved-search-digest cron.
 */
export function SaveSearchButton({ genre, bpm, keyFilter, savedSearches, refreshSaved }) {
  const { user } = useCustomerAuth();
  const [busy, setBusy] = useState(false);

  // Showing the button at "All / all / all" would clutter the UI — only once a filter is active.
  const anyFilterActive = genre !== "All" || bpm !== "all" || keyFilter !== "all";
  if (!anyFilterActive) return null;

  const match = (savedSearches || []).find(
    (s) => s.genre === genre && s.bpm === bpm && s.key === keyFilter,
  );
  const saved = Boolean(match);

  if (user === null) return null; // still checking session

  if (user === false) {
    return (
      <Link
        to="/account"
        data-testid="save-search-signin"
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/40 bg-emerald-300/5 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/15"
      >
        <Heart size={12} /> Sign in to save this pocket
      </Link>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/account/saved-searches/${match.id}`);
        toast.success("Removed saved search");
      } else {
        await api.post("/account/saved-searches", { genre, bpm, key: keyFilter });
        toast.success("Saved — we'll email you when new beats drop in this pocket");
      }
      await refreshSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that search — try again");
    } finally {
      setBusy(false);
    }
  };

  const shareSearch = { genre, bpm, key: keyFilter };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid="save-search-button"
        aria-pressed={saved}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-60 ${
          saved
            ? "border border-emerald-300 bg-emerald-300 text-black hover:bg-emerald-200"
            : "border border-emerald-300/40 bg-emerald-300/5 text-emerald-200 hover:bg-emerald-300/15"
        }`}
      >
        {busy ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <Heart size={12} fill={saved ? "currentColor" : "none"} />
        )}
        {saved ? "Saved" : "Save this search"}
      </button>
      {saved && <PocketShareButtons search={shareSearch} variant="inline" />}
    </div>
  );
}
